"use client";
import type { PlayingState } from "@ui/components/demos/VideoToMusic/types";

import { Button } from "@ui/components/core/Button";
import { presetVideos } from "@ui/components/demos/VideoToMusic/config";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import Download20 from "@ui/icons/20x20/download-20.svg";
import ElevenMusic20 from "@ui/icons/20x20/eleven-music-20.svg";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import Video20 from "@ui/icons/20x20/video-20.svg";
import Play24 from "@ui/icons/24x24/play-24.svg";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { useCallback, useRef, useState } from "react";

import { playingStateIcon } from "../../TTS/config";
import { MAX_FILE_SIZE, SUPPORTED_VIDEO_TYPES } from "../constants";

export const PresetVideoSelector = ({
  disabled,
  loadingVideoSrc,
  onVideoSelect,
}: {
  disabled?: boolean;
  loadingVideoSrc?: null | string;
  onVideoSelect: (videoSrc: string, description: string) => void;
}) => {
  return (
    <div className='space-y-20'>
      {/* Horizontal scroll on mobile, flex wrap on desktop */}
      <div className='group relative overflow-x-auto scroll-smooth md:overflow-x-visible'>
        <div className='flex gap-8 pb-4 pl-1 pr-8 md:flex-wrap md:items-center md:justify-center md:gap-12 md:pb-0 md:pl-0 md:pr-0'>
          {presetVideos.map(({ videoSrc, text, thumbnail, description }) => {
            const isLoading = loadingVideoSrc === videoSrc;
            const isDisabled = disabled || loadingVideoSrc !== null;

            return (
              <button
                className='effect-focus aspect-square relative h-80 w-80 flex-shrink-0 transform-gpu overflow-hidden rounded-lg bg-dark transition-all duration-500 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 md:h-100 md:w-100 group-hover:[&:not(:hover)]:scale-95 group-hover:[&:not(:hover)]:opacity-40'
                disabled={isDisabled}
                key={videoSrc}
                onClick={ev => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  if (!isDisabled) {
                    onVideoSelect(videoSrc, description || text);
                  }
                }}
                title={text}
              >
                <video
                  className='anim-fade-in h-full w-full object-cover transition-opacity duration-500'
                  controls={false}
                  loop
                  muted
                  onMouseEnter={e => !isDisabled && e.currentTarget.play()}
                  onMouseLeave={e => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                  }}
                  onTouchEnd={e => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                  }}
                  onTouchStart={e => !isDisabled && e.currentTarget.play()}
                  playsInline
                  poster={thumbnail}
                  preload='metadata'
                  src={videoSrc}
                >
                  <meta content={text} itemProp='description' />
                </video>

                {/* Loading overlay */}
                {isLoading && (
                  <div className='bg-black absolute inset-0 flex items-center justify-center bg-opacity-60'>
                    <div className='flex flex-col items-center gap-8' />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Scroll indicator for mobile */}
        <div className='mt-8 flex justify-center md:hidden'>
          <div className='flex space-x-2'>
            {presetVideos.map((_, index) => (
              <div className='bg-gray-300 h-2 w-2 rounded-full' key={index} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PlayButton = ({
  className,
  playingState,
  onPlay,
  variant,
  showLabel = false,
  disabled,
}: {
  className?: string;
  disabled?: boolean;
  onPlay: () => void;
  playingState: PlayingState;
  showLabel?: boolean;
  variant?: "default" | "sidebar";
}) => {
  const t = useTranslations("ClientKeys.VideoToMusic");

  return (
    <Button
      className={cn(
        "p-0",
        variant === "sidebar" ? "w-full" : showLabel ? "ml-16 w-fit px-12 pr-16" : "ml-16 w-40",
        className,
      )}
      disabled={disabled}
      onClick={onPlay}
    >
      <span className='flex items-center gap-8' key={playingState}>
        {playingStateIcon[playingState]}
        {(variant === "sidebar" || showLabel) && (playingState === "playing" ? t("pause") : t("play"))}
      </span>
    </Button>
  );
};

export const DownloadButton = ({
  audioUrl,
  canDownload,
  variant,
}: {
  audioUrl: null | string;
  canDownload: boolean;
  variant?: "default" | "sidebar";
}) => {
  const t = useTranslations("ClientKeys.VideoToMusic");

  return (
    <Button
      aria-label={t("download_generated_music")}
      className={cn("p-0", variant === "sidebar" ? "w-full" : "ml-12 w-40", !canDownload && "opacity-50")}
      disabled={!canDownload}
      onClick={() => {
        if (!audioUrl) return;

        const link = document.createElement("a");
        link.href = audioUrl;
        link.download = "ElevenLabs_Video_to_Music.wav";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }}
      variant='elevated'
    >
      <span className='flex items-center gap-8'>
        <Download20 />
        {variant === "sidebar" && t("download")}
      </span>
    </Button>
  );
};

export const GenerateButton = ({
  className,
  disabled,
  isGenerating,
  onGenerate,
}: {
  className?: string;
  disabled?: boolean;
  isGenerating?: boolean;
  onGenerate: () => void;
}) => {
  const t = useTranslations("ClientKeys.VideoToMusic");

  return (
    <Button
      className={cn("to-gray-900 hover:from-gray-900 hover:to-gray-800 bg-gradient-to-r from-black", className)}
      disabled={disabled}
      onClick={onGenerate}
      variant='primary'
    >
      <span className='flex items-center gap-8'>
        {isGenerating ? <Spinner20 /> : <Play24 />}
        {isGenerating ? t("generating") : t("generate_music")}
      </span>
    </Button>
  );
};

export const UploadButton = ({
  loadingVideoSrc,
  onFileAccepted,
  onPresetSelected,
  showPresets = true,
}: {
  loadingVideoSrc?: null | string;
  onFileAccepted: (file: File) => void;
  onPresetSelected?: (videoSrc: string, description: string) => void;
  showPresets?: boolean;
}) => {
  const t = useTranslations("ClientKeys.VideoToMusic");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const validateAndProcessFile = useCallback(
    (file: File) => {
      // Validate file type
      if (!SUPPORTED_VIDEO_TYPES.includes(file.type)) {
        alert(t("invalid_file_type"));
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        alert(t("file_too_large"));
        return;
      }

      onFileAccepted(file);
    },
    [onFileAccepted, t],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      validateAndProcessFile(file);
    },
    [validateAndProcessFile],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const file = files[0];
      if (file) {
        validateAndProcessFile(file);
      }
    },
    [validateAndProcessFile],
  );

  if (showPresets && onPresetSelected) {
    return (
      <div className='flex h-full w-full flex-col space-y-16 md:space-y-32'>
        {/* Upload Section */}
        <div
          className={cn(
            "flex flex-1 cursor-pointer flex-col items-center justify-between space-y-16 rounded-lg p-16 text-center transition-all duration-200 md:space-y-24 md:p-24",
            isDragOver ? "border-blue-400 bg-blue-50 border-2 border-dashed" : "hover:bg-gray-50",
          )}
          onClick={handleClick}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onKeyDown={e => e.key === "Enter" && handleClick()}
          role='button'
          tabIndex={0}
        >
          <div />
          <div className='flex flex-col items-center justify-center gap-16 md:gap-24'>
            {/* Music note to video icon transition */}
            <div className='flex items-center justify-center gap-12 md:gap-16'>
              <div className='bg-gray-100 flex h-20 w-20 items-center justify-center rounded-full md:h-24 md:w-24'>
                <ElevenMusic20 />
              </div>
              <ChevronRight16 className='text-gray-400' />
              <div className='bg-gray-100 flex h-20 w-20 items-center justify-center rounded-full md:h-24 md:w-24'>
                <Video20 />
              </div>
            </div>

            {/* Upload text and description */}
            <div className='space-y-8 md:space-y-12'>
              <h2 className='text-gray-900 f-heading-04'>{t("upload_video")}</h2>
              <p className='text-gray-600 mx-auto max-w-lg' style={{ fontSize: "14px", lineHeight: "1.4" }}>
                {t("upload_instruction")}
              </p>
            </div>
          </div>

          {/* File type and size info */}
          <div className='text-center'>
            <p className='text-gray-400' style={{ fontSize: "14px", lineHeight: "1.4" }}>
              {t("supported_formats")} • {t("max_size")}
            </p>
          </div>

          <input
            accept={SUPPORTED_VIDEO_TYPES.join(",")}
            className='hidden'
            onChange={handleFileSelect}
            ref={fileInputRef}
            type='file'
          />

          {/* Preset Video Selector */}
          <PresetVideoSelector loadingVideoSrc={loadingVideoSrc} onVideoSelect={onPresetSelected} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full w-full cursor-pointer flex-col items-center justify-between space-y-16 rounded-lg p-16 text-center transition-all duration-200 md:space-y-24 md:p-24",
        isDragOver ? "border-blue-400 bg-blue-50 border-2 border-dashed" : "hover:bg-gray-50",
      )}
      onClick={handleClick}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onKeyDown={e => e.key === "Enter" && handleClick()}
      role='button'
      tabIndex={0}
    >
      <div />
      <div className='flex flex-col items-center justify-center gap-16 md:gap-24'>
        {/* Music note to video icon transition */}
        <div className='flex items-center justify-center gap-12 md:gap-16'>
          <div className='bg-gray-100 flex h-20 w-20 items-center justify-center rounded-full md:h-24 md:w-24'>
            <ElevenMusic20 />
          </div>
          <ChevronRight16 className='text-gray-400' />
          <div className='bg-gray-100 flex h-20 w-20 items-center justify-center rounded-full md:h-24 md:w-24'>
            <Video20 />
          </div>
        </div>

        {/* Upload text and description */}
        <div className='space-y-8 md:space-y-12'>
          <h2 className='text-gray-900 f-heading-04'>{t("upload_video")}</h2>
          <p className='text-gray-600 mx-auto max-w-lg' style={{ fontSize: "14px", lineHeight: "1.4" }}>
            {t("upload_instruction")}
          </p>
        </div>
      </div>

      {/* File type and size info */}
      <div className='text-center'>
        <p className='text-gray-400' style={{ fontSize: "14px", lineHeight: "1.4" }}>
          {t("supported_formats")} • {t("max_size")}
        </p>
      </div>

      <input
        accept={SUPPORTED_VIDEO_TYPES.join(",")}
        className='hidden'
        onChange={handleFileSelect}
        ref={fileInputRef}
        type='file'
      />
    </div>
  );
};

export const SoundtrackOptionsSelector = ({
  description,
  onDescriptionChange,
}: {
  description: string;
  onDescriptionChange: (description: string) => void;
}) => {
  const t = useTranslations("ClientKeys.VideoToMusic");

  return (
    <div className='h-full space-y-20'>
      {/* Video Description */}
      <div className='h-full space-y-8'>
        <label className='text-gray-900 f-ui-04' htmlFor='video-description'>
          {t("video_description")}
        </label>
        <div className='relative h-full'>
          <textarea
            className='border-gray-alpha-200 bg-white focus:ring-black/20 h-full w-full resize-none rounded-xl border px-16 py-12 text-sm transition-all duration-200 focus:border-transparent focus:outline-none focus:ring-2'
            id='video-description'
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onDescriptionChange(e.target.value)}
            placeholder={t("description_placeholder")}
            value={description}
          />
        </div>
      </div>
    </div>
  );
};
